import type { GraphQLErrorOptions } from 'graphql';
import { GraphQLError } from 'graphql';
import { z } from 'zod';

export interface ErrorInterface {
  response: {
    errors: {
      data: unknown;
      extensions: { code: ErrorCodes };
      locations: unknown;
      message: string;
      path: unknown;
      status: number;
    }[];
  };
}

export enum ErrorCodes {
  BAD_USER_INPUT = 'BAD_USER_INPUT',
  /** The linked CRM record was deleted in the CRM: expected, shown to the user, never reported. */
  CRM_RECORD_NOT_FOUND = 'CRM_RECORD_NOT_FOUND',
  CRM_USER_NOT_FOUND = 'CRM_USER_NOT_FOUND',
  DOCUMENT_CHECKED_OUT = 'DOCUMENT_CHECKED_OUT',
  DOCX_PARSER_ERROR = 'DOCX_PARSER_ERROR',
  INTERNAL = 'INTERNAL',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  INVALID_CRM_SIGNATURE = 'INVALID_CRM_SIGNATURE',
  NO_DOCUMENT_ACCESS = 'NO_DOCUMENT_ACCESS',
  NOT_FOUND = 'NOT_FOUND',
  OUTDATED_DOCUMENT_VERSION = 'OUTDATED_DOCUMENT_VERSION',
  PORTAL_NOT_PAIRED = 'PORTAL_NOT_PAIRED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  UNSUPPORTED_PROTOCOL = 'UNSUPPORTED_PROTOCOL',
  UPGRADE_REQUIRED = 'UPGRADE_REQUIRED',
  USER_DEACTIVATED_ERROR = 'USER_DEACTIVATED_ERROR',
  WS_SESSION_EXPIRED = 'WS_SESSION_EXPIRED',
}

export const ErrorCodeSchema = z.enum(ErrorCodes);

/** Whether a CRM read failed because the record it names was deleted in the CRM. */
export function getIsCrmRecordNotFound(error: unknown): boolean {
  return (
    error instanceof GraphQLError && error.extensions?.code === ErrorCodes.CRM_RECORD_NOT_FOUND
  );
}

export class InternalError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.INTERNAL;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'InternalError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class DocxParserError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.DOCX_PARSER_ERROR;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'DocxParserError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class NotFoundError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.NOT_FOUND;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'NotFoundError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UnauthenticatedError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.UNAUTHENTICATED;
    super(message, { ...options, extensions: { ...options?.extensions, code } });
    Object.defineProperty(this, 'name', { value: 'UnauthenticatedError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class CrmAuthenticationError extends GraphQLError {
  constructor(message: string, code: ErrorCodes, options?: GraphQLErrorOptions) {
    super(message, { ...options, extensions: { ...options?.extensions, code } });
    Object.defineProperty(this, 'name', { value: 'CrmAuthenticationError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UnauthorizedError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.UNAUTHORIZED;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'UnauthorizedError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UnsupportedProtocolError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.UNSUPPORTED_PROTOCOL;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'UnsupportedProtocolError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class OutdatedDocumentVersionError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.OUTDATED_DOCUMENT_VERSION;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'OutdatedDocumentVersionError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class TooManyRequestsError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.TOO_MANY_REQUESTS;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'TooManyRequestsError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UserInputError extends GraphQLError {
  constructor(message: string, options?: GraphQLErrorOptions & { field?: string }) {
    const code = options?.extensions?.code ?? ErrorCodes.BAD_USER_INPUT;
    const field = options?.field;
    super(message, {
      ...options,
      extensions: { ...options?.extensions, ...(field ? { field } : {}), code },
    });
    Object.defineProperty(this, 'name', { value: 'UserInputError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class DeactivatedError extends GraphQLError {
  constructor(message: string = 'User is deactivated.', options?: GraphQLErrorOptions) {
    const code = options?.extensions?.code ?? ErrorCodes.USER_DEACTIVATED_ERROR;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'DeactivatedError' });
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UpgradeRequiredError extends GraphQLError {
  constructor(
    message: string = 'You must upgrade to a Basic user or higher to access this functionality.',
    options?: GraphQLErrorOptions
  ) {
    const code = options?.extensions?.code ?? ErrorCodes.UPGRADE_REQUIRED;
    super(message, { ...options, extensions: { code } });
    Object.defineProperty(this, 'name', { value: 'UpgradeRequiredError' });
    Error.captureStackTrace(this, this.constructor);
  }
}
